import dxDataGrid, {
    CellPreparedEvent as CellPreparedEventDataGrid,
    OptionChangedEvent as OptionChangedEventGrid,
} from 'devextreme/ui/data_grid';
import {
    CellPreparedEvent as CellPreparedEventTreeList,
    OptionChangedEvent as OptionChangedEventTree,
} from 'devextreme/ui/tree_list';
import { Workbook } from 'exceljs';
import { DataGridCell, exportDataGrid } from 'devextreme/excel_exporter';
import { saveAs } from 'file-saver-es';
import { RefObject } from 'react';
import DataGrid from 'devextreme-react/data-grid';
import { loadMessages, locale } from 'devextreme/localization';
import ruMessages from 'devextreme/localization/messages/ru.json';
import { isBefore, parseISO } from 'date-fns';
import { exportTreeList } from '@utils/devextreme/treeListExcelExporter';
import { IColumnProps, TreeList } from 'devextreme-react/tree-list';
import { ValueType } from '@/types/ValueType';
import { IGridStateSettings, IGridStateSettingsColumn } from '@models/response/IGridStateSettings';
import { IDataGridDescriptionResponse } from '@models/response/IDataGridDescriptionResponse';
import { IDictionaryData } from '@models/dictionary/IDictionaryData';
import { IDictFilter } from '@models/Forms/IForms';
import { IGridDescriptionResponse } from '@models/response/IGridDescriptionResponse';
import { IGridSettings } from '@models/dataGrid/IGridSettings';

// -------------------------------------------------------------------------------------------------------------------
// Типы данных и утилиты для фильтрации и отображения колонок
// -------------------------------------------------------------------------------------------------------------------

/**
 * Возвращает список допустимых операций фильтрации в зависимости от типа данных колонки.
 *
 * @param {string} dataType - Тип данных колонки (например, 'string', 'number', 'date', 'boolean').
 * @returns {string[]} Список поддерживаемых операций фильтрации.
 */
export const getColumnFilterOperationsByColumnDataType = (dataType: string) => {
    switch (dataType) {
        case 'date':
        case 'datetime':
            return ['=', '<>', '<', '>', 'between'];
        case 'number':
            return ['=', '<>', '<', '>', '<=', '>=', 'between'];
        case 'boolean':
            return ['='];
        default:
            return ['contains', '=', '<>'];
    }
};

/**
 * Преобразует тип данных из ValueType в строковой тип, используемый DevExtreme DataGrid.
 *
 * @param {ValueType} dataType - Тип значения поля (enum).
 * @returns {string} Строковое представление типа данных для грида: 'string', 'number', 'date', 'boolean' и т.д.
 */
export const getColumnDataTypeByFieldDataType = (dataType: ValueType) => {
    switch (dataType) {
        case ValueType.Date:
            return 'date';
        case ValueType.DateTime:
        case ValueType.NoSecDateTime:
            return 'datetime';
        case ValueType.Money:
        case ValueType.Double:
        case ValueType.Integer:
            return 'number';
        case ValueType.Boolean:
            return 'boolean';
        default:
            return 'string';
    }
};

/**
 * Определяет, должен ли быть включён Header Filter для колонки по её типу данных.
 *
 * @param {ValueType} dataType - Тип значения поля (enum).
 * @returns {boolean} True, если Header Filter должен быть включён; иначе false.
 */
export const getColumnHeaderFilterStateByFieldDataType = (dataType: ValueType) => {
    switch (dataType) {
        case ValueType.Date:
        case ValueType.DateTime:
        case ValueType.NoSecDateTime:
        case ValueType.Boolean:
            return false;
        default:
            return true;
    }
};

// -------------------------------------------------------------------------------------------------------------------
// Логика фильтрации справочников и грида (скриптовая, предикатная)
// -------------------------------------------------------------------------------------------------------------------

/**
 * Проверяет, удовлетворяет ли текущий элемент условиям фильтрации справочника.
 *
 * Учитывает включённые/исключённые значения и columnName.
 * Возвращает true, если элемент должен отображаться.
 *
 * @param {IDictionaryData} currentItem - Элемент справочника.
 * @param {any} filter - Объект фильтра с полями columnName, codes, isShow и exlude.
 * @returns {boolean} Результат фильтрации: true — элемент проходит фильтр.
 */
export const filterPredicate = (currentItem: IDictionaryData, filter: any) => {
    const value = filter.columnName
        ? currentItem.fields.find((x) => x.name === filter.columnName)?.value
        : currentItem.code;

    if (filter.exlude && filter.exlude?.codes?.indexOf(value) !== -1) return false;

    if (filter.codes?.indexOf(value) !== -1) return filter.isShow;

    return !filter.isShow;
};

/**
 * Применяет строковый фильтр из объекта IDictFilter к списку справочников.
 *
 * Фильтр передаётся в виде сериализованного JSON (возможно, с одинарными кавычками),
 * преобразуется обратно в объект, и применяется через `filterPredicate`.
 *
 * @param {IDictionaryData[]} originalItems - Исходный массив элементов справочника.
 * @param {IDictFilter} filter - Объект фильтра, содержащий строку сериализованного фильтра.
 * @returns {IDictionaryData[]} Отфильтрованный массив элементов.
 */
export const filterResponseVoid = (originalItems: IDictionaryData[], filter: IDictFilter) => {
    if (filter.filter && filter.filter != '') {
        const fObj = JSON.parse(filter.filter.replace(/\'/g, '"'));
        const filteredObject = [] as IDictionaryData[];
        for (let j = 0; j < originalItems.length; j++) {
            const item = originalItems[j];
            const filteredChild = filterPredicate(item, fObj);
            if (filteredChild) filteredObject.push(item);
        }

        return filteredObject;
    } else {
        return originalItems;
    }
};

/**
 * Применяет пользовательский скрипт-фильтр ко всем строкам справочника и возвращает отфильтрованные строки.
 *
 * @param {IDictionaryData[]} data - Массив данных справочника.
 * @param {string} script - JavaScript-выражение, которое должно быть выполнено для каждой строки.
 * @returns {IDictionaryData[]} Только те строки, для которых скрипт вернул true.
 */
export const filterGridRowsByScript = (data: IDictionaryData[], script: string) => {
    let _data: IDictionaryData[] = [];
    data.forEach((value) => {
        let result = filterGridRowByScriptEval(value, data, script);
        if (result) {
            _data.push(value);
        }
    });
    return _data;
};

/**
 * Проверяет, есть ли в справочнике хотя бы одна строка, удовлетворяющая скрипту.
 *
 * @param {IDictionaryData[]} data - Массив данных справочника.
 * @param {string} script - JavaScript-выражение для проверки.
 * @returns {boolean} False, если хотя бы одна строка прошла фильтр (то есть нужна проверка), иначе true.
 */
export const checkGridRowsByScript = (data: IDictionaryData[], script: string) => {
    for (let index = 0; index < data.length; index++) {
        const value = data[index];
        let result = filterGridRowByScriptEval(value, data, script);
        if (result) {
            return false;
        }
    }
    return true;
};

/**
 * Выполняет JavaScript-выражение для одной строки справочника и возвращает результат.
 * Скрипт имеет доступ к вспомогательным функциям: `field`, `code`, `columnValues`, `intersect`, `getDateInt`, и др.
 *
 * ⚠ ВНИМАНИЕ: Используется eval. Код должен быть проверен и безопасен!
 *
 * @param {IDictionaryData} data - Текущая строка справочника.
 * @param {IDictionaryData[]} dataArray - Все строки (для получения агрегатных значений, если нужно).
 * @param {string} script - Скрипт, возвращающий логическое значение.
 * @returns {boolean} Результат выполнения скрипта.
 */
export const filterGridRowByScriptEval = (data: IDictionaryData, dataArray: IDictionaryData[], script: string) => {
    /**
     * WARNING! Begin sections of functions for templates, do not rename
     * */
    let subFunc = ` 
    function code (){
        return data.code;
    };

    function getDateInt (data) {
        let date = stringToDateFormat(data.split(' ')[0], 'dd.mm.yyyy');
        return date.getTime();
    };

    function getOnlyDateFromInt (data) {
        let date = new Date(+data);
        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0).getTime();
    };

    function field (name) {
        let value = data.fields.find((field) => field.name === name)?.value;
        return value;
    };

    function columnValues (name) {
        let arr = [];
        dataArray.forEach((row)=>{
            let value = row.fields.find((field) => field.name === name)?.value;
            arr.push(value);
        });
        return arr;
    };
    function eliminateDuplicates(arr) {
        let i;
         let   len = arr.length;
         let   out = [];
         let  obj = {};
      
        for (i = 0; i < len; i++) {
          obj[arr[i]] = 0;
        }
        for (i in obj) {
          out.push(i);
        }
        return out;
      };

    function intersect (array1, array2){
        return array1 && array2 && array1.some((item) => array2.includes(item));
    };
`;

    return eval(subFunc + script);
};

// -------------------------------------------------------------------------------------------------------------------
// Состояние грида (применение, экспорт, обновление)
// -------------------------------------------------------------------------------------------------------------------

/**
 * Применяет сохранённое состояние грида к текущей конфигурации колонок (видимость, порядок, ширина).
 *
 * @param {IGridStateSettings | undefined} settings - Объект с сохранённым состоянием грида.
 * @param {IGridDescriptionResponse | undefined} gridDesc - Описание колонок грида.
 * @param {React.RefObject<any>} objRef - Ссылка на экземпляр компонента грида.
 */
export const applyGridState = (
    settings: IGridStateSettings | undefined,
    gridDesc: IGridDescriptionResponse | undefined,
    objRef: React.RefObject<any>,
) => {
    if (settings) {
        gridDesc?.columns.forEach((column, index) => {
            let options = objRef.current?.instance.columnOption(index) as IColumnProps;

            const stateColumn = settings?.state.columns.filter(
                (x) => x.dataField?.toUpperCase() === options.dataField?.toUpperCase(),
            )[0];

            if (stateColumn !== undefined) {
                options.visible = stateColumn.visible;
                options.visibleIndex = stateColumn.visibleIndex;
                options.width = stateColumn.width;
            } else {
                options.visible = false;
            }

            objRef.current?.instance.columnOption(index, options);
        });
    }
};

/**
 * Асинхронно применяет состояние грида: сбрасывает фильтры и сортировку, применяет колонки и фильтры с задержкой.
 *
 * @param {dxDataGrid | undefined} grid - Экземпляр DevExtreme DataGrid.
 * @param {IGridStateSettings | undefined} settings - Состояние, которое нужно применить.
 */
export const applyStateSettings = async (grid?: dxDataGrid, settings?: IGridStateSettings) => {
    if (grid && settings) {
        grid.clearFilter();
        grid.clearSorting();

        // Ждём завершения операций с фильтрами/сортировкой
        await delay();
        applyColumnSettings(grid, settings);
        // Применяем фильтры с задержкой
        await delay();
        applyFilters(grid, settings);
    }
};

const delay = (ms?: number) => new Promise((resolve) => setTimeout(resolve, ms));

const applyColumnSettings = (grid: dxDataGrid, settings: IGridStateSettings) => {
    const columnsSettings = new Map<string, IGridStateSettingsColumn>();
    settings.state.columns.forEach((column) => {
        columnsSettings.set(column.dataField ? column.dataField?.toLowerCase()! : column.name?.toLowerCase()!, column);
    });

    for (let i = 0; i < grid.columnCount()!; ++i) {
        const column = grid.columnOption(i) as IColumnProps;
        if (!column) {
            continue;
        }

        if (column.dataField === 'actionColumn' || column.dataField === 'emptyColumn') {
            continue;
        }

        let columnSettings = column.dataField ? columnsSettings.get(column.dataField.toLowerCase()) : undefined;
        if (!columnSettings) {
            columnSettings = column.caption ? columnsSettings.get(column.caption.toLowerCase()) : undefined;
        }
        if (columnSettings) {
            column.visible = columnSettings.visible;
            column.visibleIndex = columnSettings.visibleIndex;
            if (columnSettings.width) {
                column.width = columnSettings.width;
            }
            column.sortIndex = columnSettings.sortIndex;
            column.sortOrder = columnSettings.sortOrder;
        } else {
            column.visible = false;
        }
        grid.columnOption(i, column);
    }
};

const applyFilters = (grid: dxDataGrid, settings: IGridStateSettings) => {
    if (settings.filters) {
        grid.option('filterValue', settings.filters);
    }
};

/**
 * Возвращает выбранное или дефолтное состояние грида из массива сохранённых состояний.
 *
 * @param {IGridStateSettings[]} [settings] - Массив доступных состояний грида.
 * @returns {IGridStateSettings | undefined} Найденное состояние или undefined.
 */
export const getStateSettings = (settings?: IGridStateSettings[]) => {
    if (settings) {
        let state = settings.find((x) => x?.selected);
        if (!state) {
            state = settings.find((x) => x?.default);
        }
        return state;
    }
    return undefined;
};

/**
 * Генерирует дефолтное состояние грида на основе описания колонок.
 *
 * @param {IDataGridDescriptionResponse | undefined} description - Описание колонок грида.
 * @returns {IGridStateSettings | undefined} Сформированное состояние или undefined.
 */
export const getOriginalState = (description?: IDataGridDescriptionResponse) => {
    if (description) {
        let st: IGridStateSettings = {
            default: true,
            name: 'default',
            selected: false,
            state: {
                columns: [],
                allowedPageSizes: [10, 20, 30],
                filterPanel: {
                    filterEnabled: false,
                },
                filterValue: '',
                pageIndex: 0,
                pageSize: 10,
                searchText: '',
            },
            filters: undefined,
            sort: undefined,
        };

        description?.columns?.forEach((column, index) => {
            if (column.field !== 'emptyColumn' && !column.defaultHide) {
                st.state.columns.push({
                    dataField: column.field,
                    dataType: column.dataType,
                    name: column.header,
                    visible: !column.defaultHide,
                    visibleIndex: index,
                    width: column.width,
                });
            }
        });

        return st.state.columns.length > 0 ? st : undefined;
    }
    return undefined;
};

/**
 * Включает пакетное обновление грида (beginUpdate), предотвращая множественные перерисовки.
 *
 * @param {dxDataGrid | undefined} grid - Экземпляр грида.
 * @param {React.RefObject<boolean>} isUpdating - Флаг текущего состояния обновления.
 */
export const updateModeOn = (grid: dxDataGrid | undefined, isUpdating: React.RefObject<boolean>) => {
    if (grid) {
        if (!isUpdating.current) {
            isUpdating.current = true;
            grid?.beginUpdate();
        }
    }
};

/**
 * Завершает пакетное обновление грида (endUpdate), включая обновление визуального состояния.
 *
 * @param {dxDataGrid | undefined} grid - Экземпляр грида.
 * @param {React.RefObject<boolean>} isUpdating - Флаг текущего состояния обновления.
 */
export const updateModeOff = (grid: dxDataGrid | undefined, isUpdating: React.RefObject<boolean>) => {
    if (grid) {
        if (isUpdating.current) {
            isUpdating.current = false;
            grid?.endUpdate();
        }
    }
};

// -------------------------------------------------------------------------------------------------------------------
// Работа с датами и фильтрацией по дате
// -------------------------------------------------------------------------------------------------------------------

/**
 * Добавляет указанное количество секунд к дате.
 *
 * @param {Date} date - Исходная дата.
 * @param {number} seconds - Количество секунд для добавления.
 * @returns {Date} Новая дата с учётом добавленных секунд.
 */
export const addSeconds = (date: Date, seconds: number): Date => {
    let _date = new Date(date);
    _date.setTime(_date.getTime() + seconds * 1000);
    return _date;
};

/**
 * Преобразует строку в дату без учёта часового пояса (локализованная дата).
 * Используется для корректного сравнения дат при фильтрации.
 *
 * @param {string} date - Строка в формате ISO или подобном.
 * @returns {Date} Дата с компенсацией временной зоны.
 */
export const parseDateWithoutTz = (date: string): Date => {
    let _date = new Date(date);
    return addSeconds(_date, -_date.getTimezoneOffset() * 60);
};

/**
 * Возвращает дату в ISO-формате (только дата, без времени).
 *
 * @param {Date} date - Объект Date.
 * @returns {string} Строка в формате YYYY-MM-DD.
 */
export const getIsoDate = (date: Date): string => {
    return date.toISOString().substring(0, 10);
};

/**
 * Формирует выражение фильтрации для DevExtreme по типу поля, значению и выбранной операции.
 *
 * Поддерживает типы данных: boolean, date.
 * Для дат учитывается диапазон и сравнение с точностью до дня.
 *
 * @param {*} this - Контекст поля, содержащий dataType, dataField и defaultCalculateFilterExpression.
 * @param {*} filterValue - Значение фильтра (может быть строкой, датой или массивом).
 * @param {*} selectedFilterOperation - Операция фильтрации (например, '=', '<>', 'between').
 * @param {...any[]} args - Прочие параметры.
 * @returns {any} Выражение для фильтрации, совместимое с DevExtreme.
 */
export function calculateFilterExpression(this: any, filterValue: any, selectedFilterOperation: any, ...args: any[]) {
    if (typeof filterValue == 'string') {
        filterValue = filterValue.trim();
    }

    // substitute for null values
    if (filterValue === '(null)' && (selectedFilterOperation === '=' || selectedFilterOperation === '<>')) {
        return [this.dataField, selectedFilterOperation, null];
    }

    if (this.dataType === 'boolean') {
        return [this.dataField, filterValue ? '=' : '<>', 1];
    }

    if (this.dataType === 'date') {
        if (Array.isArray(filterValue)) {
            if (selectedFilterOperation === 'between') {
                if (filterValue.length === 2 && filterValue[0] && filterValue[1]) {
                    let fr = parseDateWithoutTz(filterValue[0]);
                    let to = parseDateWithoutTz(filterValue[1]);
                    return [[this.dataField, '>', getIsoDate(fr)], 'and', [this.dataField, '<', getIsoDate(to)]];
                }
            }
        } else {
            if (selectedFilterOperation === '=') {
                let fr = parseDateWithoutTz(filterValue);
                let to = addSeconds(fr, 24 * 60 * 60);
                return [[this.dataField, '>=', getIsoDate(fr)], 'and', [this.dataField, '<', getIsoDate(to)]];
            } else if (selectedFilterOperation === '<>') {
                let fr = parseDateWithoutTz(filterValue);
                let to = addSeconds(fr, 24 * 60 * 60);
                return [[this.dataField, '<', getIsoDate(fr)], 'or', [this.dataField, '>=', getIsoDate(to)]];
            } else {
                let date = parseDateWithoutTz(filterValue);
                return [this.dataField, selectedFilterOperation, getIsoDate(date)];
            }
        }
    }

    // Invokes the default filtering behavior
    return this.defaultCalculateFilterExpression.call(this, filterValue, selectedFilterOperation, ...args);
}

// -------------------------------------------------------------------------------------------------------------------
// Поведение ячеек и DOM (атрибуты, тултипы)
// -------------------------------------------------------------------------------------------------------------------

/**
 * Устанавливает текст ячейки в атрибут `title`, если это не детализированная строка.
 * Используется для отображения подсказок по наведению.
 *
 * @param {any} e - Событие ячейки (CellHover / CellPrepared).
 */
export const onCellHoverChanged = (e: any) => {
    // для masterDetail ячеек тултип не показываем
    if (e.row && e.row.rowType === 'detail') return;

    e.cellElement.setAttribute('title', e.cellElement.innerText);
};

/**
 * Добавляет в ячейку атрибут `data-value`, содержащий текст ячейки.
 * Используется, например, для автотестов или постобработки.
 *
 * @param {CellPreparedEventDataGrid | CellPreparedEventTreeList} e - Событие подготовки ячейки.
 */
export const setDataValueAttr = (e: CellPreparedEventDataGrid | CellPreparedEventTreeList) => {
    // записываем значение ячейки в кастомный data-value атрибут ячейки
    if (!e.cellElement.getAttribute('data-value')) {
        e.cellElement.setAttribute('data-value', e.text);
    }
};

/**
 * Добавляет к строке атрибут `data-key` со значением ключа строки.
 * Применимо к строкам в гриде или древовидном списке.
 *
 * @param {CellPreparedEventDataGrid | CellPreparedEventTreeList} e - Событие подготовки ячейки.
 */
export const setDataKeyAttr = (e: CellPreparedEventDataGrid | CellPreparedEventTreeList) => {
    // записываем ключ элемента в кастомный data-key атрибут элемента строки
    if (!e.cellElement.parentElement?.getAttribute('data-key')) {
        e.cellElement.parentElement?.setAttribute('data-key', e.key);
    }
};

// -------------------------------------------------------------------------------------------------------------------
// Типы и функции для экспорта DevExtreme DataGrid и TreeList в Excel
// -------------------------------------------------------------------------------------------------------------------

/**
 * Свойства для кастомизации ячеек Excel при экспорте.
 */
export interface CustomizeExcelCellProps {
    gridCell?: DataGridCell;
    excelCell?: any;
}

/**
 * Тип функции для пользовательской обработки ячеек Excel.
 */
type CustomizeExcelCell = (options: CustomizeExcelCellProps) => void;

const booleanToRu = (v: unknown) => {
    if (v === true) return 'Да';
    if (v === 1) return 'Да';

    if (typeof v === 'string') {
        const s = v.trim();
        if (s === '1') return 'Да';
    }

    return 'Нет';
};

/**
 * Выгружает DataGrid в Excel-файл.
 *
 * @param {RefObject<DataGrid | null>} gridRef - Ссылка на компонент DevExtreme DataGrid.
 * @param {string} fileName - Имя файла Excel без расширения.
 * @param {CustomizeExcelCell} [customizeCell] - Опциональная функция кастомизации ячеек Excel.
 */
export const exportGridToExcel = (
    gridRef: RefObject<DataGrid | null>,
    fileName: string,
    customizeCell?: CustomizeExcelCell,
) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Лист 1');

    exportDataGrid({
        component: gridRef.current?.instance,
        worksheet,
        autoFilterEnabled: true,
        customizeCell(options) {
            const { gridCell, excelCell } = options;

            if (gridCell?.rowType === 'data' && gridCell.column?.dataType === 'boolean') {
                excelCell.value = booleanToRu(gridCell.value);
            }

            if (customizeCell) {
                customizeCell(options);
            }
        },
    }).then(() => {
        workbook.xlsx.writeBuffer().then((buffer: any) => {
            saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${fileName}.xlsx`);
        });
    });
};

/**
 * Выгружает TreeList в Excel-файл.
 *
 * @param {RefObject<TreeList | null>} treeRef - Ссылка на компонент DevExtreme TreeList.
 * @param {string} fileName - Имя файла Excel без расширения.
 */
export const exportTreeListToExcel = (treeRef: RefObject<TreeList | null>, fileName: string) => {
    if (!treeRef.current?.instance) return;
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Лист 1');

    exportTreeList({
        component: treeRef.current.instance,
        worksheet,
    }).then(() => {
        workbook.xlsx.writeBuffer().then((buffer: any) => {
            saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${fileName}.xlsx`);
        });
    });
};

// -------------------------------------------------------------------------------------------------------------------
// Инициализация локализованных сообщений
// -------------------------------------------------------------------------------------------------------------------

/**
 * Инициализирует локализацию DevExtreme: подключает русские сообщения и устанавливает локаль `ru`.
 */
export const initMessages = () => {
    loadMessages({
        ru: {
            true: 'Да',
            false: 'Нет',
        },
    });
    loadMessages(ruMessages);
    locale('ru');
};

// -------------------------------------------------------------------------------------------------------------------
// Функции для определения состояния срока выполнения задачи
// -------------------------------------------------------------------------------------------------------------------

/**
 * Проверяет, считается ли задача «почти просроченной» по умолчанию.
 * Считается таковой, если осталось меньше половины времени между startTime и dueTime.
 *
 * @param {Date | null} startTime - Время начала задачи.
 * @param {Date | null} dueTime - Дедлайн задачи.
 * @returns {boolean} True, если задача почти просрочена.
 */
function defaultIsPreOverdue(startTime: Date | null, dueTime: Date | null): boolean {
    if (dueTime === null) return false;
    if (startTime === null) return false;

    const now = new Date();

    const totalTime = dueTime.getTime() - startTime.getTime();
    const timeLeft = dueTime.getTime() - now.getTime();

    return timeLeft > 0 && totalTime > 0 && timeLeft <= totalTime / 2;
}

/**
 * Текущая используемая функция для определения "почти просроченности".
 * Может быть переопределена при динамической загрузке.
 *
 * @type {(startTime: Date | null, dueTime: Date | null) => boolean}
 */
let isPreOverdueFn: (startTime: Date | null, dueTime: Date | null) => boolean = defaultIsPreOverdue;

/**
 * Загружает внешнюю реализацию isPreOverdue из JS-файла (например, для пользовательской логики).
 * При успешной загрузке заменяет `isPreOverdueFn` на кастомную реализацию.
 *
 * @async
 * @returns {Promise<(startTime: Date | null, dueTime: Date | null) => boolean>} Загруженная или дефолтная функция.
 */
async function loadIsPreOverdue(): Promise<(startTime: Date | null, dueTime: Date | null) => boolean> {
    try {
        const res = await fetch('/Scripts/isPreOverdueFn.js');
        if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);

        const contentType = res.headers.get('Content-Type');
        if (!(contentType === 'text/javascript' || contentType === 'application/javascript')) {
            throw new Error(`isPreOverdueFn.js is not a JS file`);
        }

        const scriptText = await res.text();

        eval(scriptText);

        const fn = (self as any).__taskDueTimeUtils?.isPreOverdue;
        if (typeof fn === 'function') {
            isPreOverdueFn = fn;
        }
    } catch (err) {
        console.info('Script load failed, using fallback', err);
    }

    return isPreOverdueFn;
}
await loadIsPreOverdue();

/**
 * Определяет CSS-класс задачи в зависимости от даты дедлайна и состояния.
 * Классы:
 * - "task-overdue-row" — дедлайн в прошлом
 * - "task-pre-overdue-row" — почти просрочено (по логике `isPreOverdueFn`)
 * - "task-normal-row" — всё в порядке или бессрочная задача
 *
 * @param {string} startTimeStr - Строка с ISO-датой начала задачи (может быть пустой).
 * @param {string} dueTimeStr - Строка с ISO-датой дедлайна (может быть пустой).
 * @returns {Promise<string>} CSS-класс для отображения строки задачи.
 */
export const getTaskDueTimeClass = async (startTimeStr: string, dueTimeStr: string): Promise<string> => {
    if (!dueTimeStr) {
        // бессрочная задача
        return 'task-normal-row';
    } else {
        const dueTime = parseISO(dueTimeStr);
        const now = new Date();

        if (isBefore(dueTime, now)) {
            // просроченная задача
            return 'task-overdue-row';
        }

        const startTime = startTimeStr ? parseISO(startTimeStr) : null;
        const isAlmostDue = isPreOverdueFn(startTime, dueTime);

        return isAlmostDue ? 'task-pre-overdue-row' : 'task-normal-row';
    }
};

// -------------------------------------------------------------------------------------------------------------------

/**
 * Проверяет, нужно ли показывать кнопку "Очистить фильтры" в DevExtreme Grid или TreeList.
 * Возвращает кортеж, где:
 * - первый элемент — признак, что изменение связано с фильтрацией;
 * - второй элемент — есть ли в данный момент активные фильтры (объединённое условие фильтрации).
 *
 * @param {OptionChangedEventGrid<any, string> | OptionChangedEventTree<any, string>} e - Событие изменения опции компонента Grid или TreeList.
 * @returns {[boolean, boolean]} Кортеж из [isFilterChanged, hasActiveFilters].
 */
export const checkGridFilterValue = (e: OptionChangedEventGrid<any, string> | OptionChangedEventTree<any, string>) => {
    if (
        e.fullName.endsWith('.filterValue') ||
        e.fullName.endsWith('.selectedFilterOperation') ||
        e.fullName.endsWith('.filterValues') ||
        e.fullName.endsWith('.filterType') ||
        e.fullName === 'searchPanel.text'
    ) {
        return [true, !!e.component.getCombinedFilter()];
    }

    return [false, false];
};

export const readUserDefaultKey = (searchForm: string): string | undefined => {
    try {
        const raw = localStorage.getItem(`grid.settings.${searchForm}`);
        return raw ? (JSON.parse(raw) as IGridSettings).defaultPredefinedFilter : undefined;
    } catch {
        return undefined; // битые данные в LS не должны ломать рендер
    }
};
